import React, { useEffect, useState } from 'react';
import Layout from './components/Layout';
import ResponsiveShadow from './components/ResponsiveShadow';
import PageHeader from './components/PageHeader';
import PageContent from './components/PageContent';
import TableContainer from './components/TableContainer';
import DefaultModal from './components/modals/DefaultModal';
import ConfirmModal from './components/modals/ConfirmModal';
import useDefaultModal from './components/hooks/useDefaultModal';
import useConfirmModal from './components/hooks/useConfirmModal';
import PaginationBar from './components/PaginationBar';
import usePagination10 from './components/hooks/usePagination10';
import DataTable from './components/DataTable';
import axios from 'axios';
import { API_BASE_URL } from '../config/ApiConfig';

function Emissores() {
  const [list, setList] = useState([]);
  const [isLoading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [filtro, setFiltro] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'codigo', direction: 'asc' });
  const token = localStorage.getItem('token');

  const normalizeString = str => {
    if (!str) return '';
    return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase();
  };

  const sortedList = [...list].sort((a, b) => {
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    return String(a[sortConfig.key]).localeCompare(String(b[sortConfig.key]), 'pt-BR') * dir;
  });

  const filteredList = sortedList.filter(row => {
    const termo = normalizeString(filtro);
    return (
      normalizeString(row.codigo).includes(termo) ||
      normalizeString(row.nome).includes(termo)
    );
  });

  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();
  const { setPagination } = usePagination10(filteredList.length);

  const paginatedList = filteredList.slice(
    setPagination.startIndex,
    setPagination.startIndex + setPagination.rowsPerPage
  );

  const load = () => {
    setLoading(true);
    axios.get(API_BASE_URL.EMISSOR, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => setList(Array.isArray(res.data) ? res.data : []))
      .catch(err => showDefaultModal('error', err?.response?.data?.error || 'Erro ao carregar emissores'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const sortIcon = (key) => {
    if (sortConfig.key !== key) return <i className="bi bi-arrow-down-up ms-1" style={{ opacity: 0.3 }} />;
    return sortConfig.direction === 'asc'
      ? <i className="bi bi-sort-up ms-1" />
      : <i className="bi bi-sort-down ms-1" />;
  };

  useEffect(() => {
    setPagination.onFirstItems();
  }, [filtro]);

  const handleNew = () => { setSelected(null); setShowModal(true); };
  const handleEdit = () => {
    if (!selectedRow) return;
    setSelected(selectedRow);
    setShowModal(true);
  };
  const handleDelete = () => {
    if (!selectedRow) return;
    showConfirmModal('delete', async () => {
      try {
        await axios.delete(`${API_BASE_URL.EMISSOR}/${selectedRow.codigo}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        showDefaultModal('success', 'Emissor excluído com sucesso!');
        setSelectedRow(null);
        load();
      } catch (error) {
        const msg = error?.response?.data?.error || 'Erro ao excluir emissor.';
        showDefaultModal('error', msg);
      }
    });
  };

  const handleSaved = () => {
    setShowModal(false);
    load();
  };

  const handleRowClick = (row) => {
    setSelectedRow(selectedRow?.codigo === row.codigo ? null : row);
  };

  return (
    <Layout>
      <ResponsiveShadow>
        <PageHeader title="Órgãos Emissores" icon="bi-file-earmark-code" />
        <PageContent>
          <div className="d-flex flex-column flex-lg-row justify-content-lg-between gap-2 mb-2 align-items-start align-items-lg-center">
            <div className="botao-grid-2col">
              <button className="btn btn-success btn-sm botao-igual" onClick={handleNew}>
                <i className="bi bi-plus-circle me-1" /> Novo
              </button>
              <button className="btn btn-primary btn-sm botao-igual" onClick={handleEdit} disabled={!selectedRow}>
                <i className="bi bi-pencil me-1" /> Editar
              </button>
              <button className="btn btn-danger btn-sm botao-igual" onClick={handleDelete} disabled={!selectedRow}>
                <i className="bi bi-trash me-1" /> Excluir
              </button>
            </div>
            <div className="input-group input-group-sm mt-2 mt-lg-0" style={{ maxWidth: '300px' }}>
              <span className="input-group-text">
                <i className="bi bi-search"></i>
              </span>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Buscar por código ou nome"
                value={filtro}
                onChange={e => setFiltro(e.target.value.toUpperCase())}
              />
            </div>
          </div>

          <TableContainer isLoading={isLoading}>
            <DataTable style={{ fontSize: '0.8rem' }} head={
              <tr>
                <th style={{ width: '150px', cursor: 'pointer' }} onClick={() => handleSort('codigo')}>
                  Código {sortIcon('codigo')}
                </th>
                <th style={{ cursor: 'pointer' }} onClick={() => handleSort('nome')}>
                  Nome {sortIcon('nome')}
                </th>
              </tr>}>
              <tbody>
                {paginatedList && paginatedList.length > 0 ? paginatedList.map((row) => (
                  <tr
                    key={row.codigo}
                    onClick={() => handleRowClick(row)}
                    className={selectedRow?.codigo === row.codigo ? 'table-primary' : ''}
                    style={{ cursor: 'pointer' }}
                  >
                    <td className="font-monospace">{row.codigo}</td>
                    <td>{row.nome}</td>
                  </tr>
                )) : (
                  <tr><td colSpan={2} className="text-center">Nenhum registro encontrado.</td></tr>
                )}
              </tbody>
            </DataTable>
          </TableContainer>
        </PageContent>
        <PaginationBar setPagination={setPagination} />
      </ResponsiveShadow>

      <EmissorModal
        show={showModal}
        onClose={() => setShowModal(false)}
        onSave={handleSaved}
        data={selected}
      />
      <DefaultModal {...setDefaultModal} />
      <ConfirmModal {...setConfirmModal} />
    </Layout>
  );
}

function EmissorModal({ show, onClose, onSave, data }) {
  const [formData, setFormData] = useState({ codigo: '', nome: '' });

  useEffect(() => {
    if (show && data) setFormData({ codigo: data.codigo || '', nome: data.nome || '' });
  }, [show, data]);

  function handleChange(e) {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!formData.codigo.trim()) {
      alert('Código é obrigatório.');
      return;
    }
    if (!formData.nome.trim()) {
      alert('Nome é obrigatório.');
      return;
    }
    try {
      if (data?.codigo) {
        await axios.put(`${API_BASE_URL.EMISSOR}/${data.codigo}`, formData, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
      } else {
        await axios.post(API_BASE_URL.EMISSOR, formData, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
      }
      onSave();
    } catch (error) {
      const msg = error?.response?.data?.error || 'Erro ao salvar emissor.';
      alert(msg);
    }
  }

  if (!show) return null;

  return (
    <div
      className="modal show d-block"
      tabIndex="-1"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
    >
      <div className="modal-dialog modal-dialog-centered modal-sm">
        <div className="modal-content">
          <div
            className="modal-header text-white"
            style={{
              background: 'linear-gradient(45deg, rgb(7, 29, 65) 0%, rgba(29, 100, 216, 1) 100%)',
              padding: '0.5rem 1rem',
            }}
          >
            <h6 className="modal-title mb-0 small">
              <i className="bi bi-file-earmark-code me-2"></i>
              {data?.codigo ? 'Editar Emissor' : 'Novo Emissor'}
            </h6>
            <button type="button" className="btn-close btn-close-white" onClick={onClose} />
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="mb-3">
                <label className="form-label small fw-semibold">Código</label>
                <input
                  type="text"
                  className="form-control"
                  name="codigo"
                  value={formData.codigo}
                  onChange={handleChange}
                  maxLength={15}
                  placeholder="Ex: SSP"
                  readOnly={!!data?.codigo}
                />
              </div>
              <div className="mb-3">
                <label className="form-label small fw-semibold">Nome</label>
                <input
                  type="text"
                  className="form-control"
                  name="nome"
                  value={formData.nome}
                  onChange={handleChange}
                  maxLength={100}
                  placeholder="Nome do órgão emissor"
                />
              </div>
            </div>
            <div className="modal-footer" style={{ padding: '0.5rem 1rem' }}>
              <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>
                Cancelar
              </button>
              <button type="submit" className="btn btn-primary btn-sm">
                Salvar
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Emissores;
